/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-danger */
import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Document } from 'api/document/types/document';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { OnUserMention } from 'components/molecules/mention';
import { CKCollabEditor } from 'components/organisms/ck-collab-editor';
import { EditorsObject } from 'components/organisms/ck-collab-editor/custom-editor.types';
import { getCkEditorUploadConfig } from 'components/organisms/ck-collab-editor/helpers';
import {
  saveAllEditors,
  setActiveEditor,
  useDocuments,
} from 'components/organisms/document/document-store';
import { Topbar } from 'components/organisms/document/parts/topbar';
import { statusUpdateHeaderTools } from 'components/organisms/document/parts/topbar/tools';
import { EditorModes } from 'data/documents';
import { setDirtyState } from 'data/templates';
import { TemplateObjectType } from 'data/templates/api';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { ConditionalVariables } from 'vendor/ckeditor/plugins/variables/conditional-variables';
import { SimpleVariables } from 'vendor/ckeditor/plugins/variables/simple-variables';
import { CKMentionPopup } from 'vendor/ckeditor/react-components/mention';
import { CKVariablesPopup } from 'vendor/ckeditor/react-components/variables';
import { useStatusUpdatesTemplatesContext } from '../context';
import {
  ResourceListTemplateSection,
  ResourceTemplateSectionSchema,
} from '../resource-list-templates-section';
import { resourceSectionTemplateRenderer } from '../resource-section-template-renderer';
import { EditorHeader } from '../su-template-editor-header';
import { EditorWrapper, MentionPopup, Container } from './styles';

const resourceListConfig = {
  [ResourceTemplateSectionSchema]: {
    renderer: resourceSectionTemplateRenderer,
  },
};

declare global {
  interface Window {
    RLStatusUpdateStore: {
      editor: Editor;
    };
  }
}

export const StatusUpdateEditor = ({
  onUserMention,
}: {
  onUserMention: OnUserMention;
}): JSX.Element => {
  const dispatch = useDispatch();
  const refMentionPopup = useRef<HTMLDivElement | null>(null);
  const [mentionPopupPosition, setMentionPopupPosition] = useState({
    top: 0,
    left: 0,
  });

  const { editorContent, setEditorContent, templateId } =
    useStatusUpdatesTemplatesContext();

  const [editor, setEditor] = useState<Editor | null>(null);

  const apiService = {
    getDocument: () => {
      const document = {
        title: 'Untitled',
        sections: [
          {
            meta: {
              sectionType: 'PUBLIC',
            },
            content: editorContent,
            createdAt: 1635220858446,
            documentSectionId: 1635220859632,
          },
        ],
      } as AtLeast<Document, 'title' | 'documentId' | 'sections'>;

      return Promise.resolve({
        document,
      });
    },
  };

  const mentionsData = useTemplateMentionsData();

  const presenceListElementRef = useRef<HTMLDivElement>(null);

  const [state, documentStoreDispatch] = useDocuments();

  const onEditorReady = (allEditors: EditorsObject) => {
    if (allEditors[EditorModes.Public]) {
      setEditor(allEditors[EditorModes.Public]);
      setActiveEditor(documentStoreDispatch, allEditors[EditorModes.Public]);
    }
    saveAllEditors(documentStoreDispatch, allEditors);
  };

  const extraConfig = {
    autosave: {
      waitingTime: 2000,
      save(edtr) {
        const content = edtr.getData();

        if (!templateId) {
          return Promise.resolve();
        }

        if (content !== editorContent) {
          setEditorContent(content);
          dispatch(
            setDirtyState({
              id: templateId,
              _state: true,
            }),
          );
        }

        return Promise.resolve();
      },
    },
    ...getCkEditorUploadConfig(),
  };

  const onEditorFocusChange = (editorT: Editor) => {
    setActiveEditor(documentStoreDispatch, editorT);
  };

  return (
    <Container>
      <EditorHeader />
      <Topbar
        editor={state.editor}
        showDocTitle={false}
        docTitle=""
        tools={statusUpdateHeaderTools}
      />
      <CKMentionPopup
        mentionData={mentionsData}
        editor={editor}
        attachTo={refMentionPopup.current}
        onUserMention={onUserMention}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
      >
        <MentionPopup
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
        />
      </CKMentionPopup>

      <CKVariablesPopup
        editor={editor}
        attachTo={refMentionPopup.current}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
        templateType={TemplateObjectType.StatusUpdates}
        allowConditionalVariables
      >
        <MentionPopup
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
        />
      </CKVariablesPopup>

      <EditorWrapper>
        <ErrorBoundary
          fallback={() => (
            <p>
              <FormattedMessage id="editor.error" />
            </p>
          )}
        >
          {templateId ? (
            <CKCollabEditor
              apiService={apiService}
              className="status-update__editor"
              onEditorReady={onEditorReady}
              singleUserMode
              customClasses={{
                editorClass: 'status_template__editor',
              }}
              extraPlugins={[
                ResourceListTemplateSection,
                SimpleVariables,
                ConditionalVariables,
              ]}
              extraConfig={{ ...resourceListConfig, ...extraConfig }}
              presenceListElementRef={presenceListElementRef}
              docId={templateId}
              onEditorFocusChange={onEditorFocusChange}
            />
          ) : null}
        </ErrorBoundary>
      </EditorWrapper>
    </Container>
  );
};
